// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import Dropzone from 'react-dropzone';
import { FormattedMessage, intlShape } from 'react-intl';
import { Element } from 'react-scroll';
import icons from '../../../../../../assets/styles/icons.scss';
import InputPrice from '../../../../../../elements/InputPrice/InputPrice';
import InputQuantityRound from '../../../../../../elements/InputQuantityRound/InputQuantityRound';
import InputWeight from '../../../../../../elements/InputWeight/InputWeight';
import Loading from '../../../../../../elements/Loading/Loading';
import Select from '../../../../../../elements/Select/Select';
import { FormValidator } from '../../../../../../utils/form';
import DetailOption from '../../DetailOption/DetailOption';
import VolumetricOptions from '../../ProductForm/VolumetricOptions/VolumetricOptions';
import Tooltip from '../../../../../../elements/Tooltip/Tooltip';
import ErrorLinkModal from '../ErrorLinkModal/ErrorLinkModal';
import EbayInformationModal from '../EbayInformationModal/EbayInformationModal';
import { ebayUrlRegex } from '../../../../../../utils/string';
import picture from '../../../../../../assets/img/new-order/product-step/picture.png';

import formStyle from '../../../../../../assets/styles/form.scss';
import inputStyle from '../../../../../../assets/styles/input.scss';
import style from './ProductBox.scss';

import type { Props as NewProductFormProps } from '../NewProductForm';
import type { Option } from '../../../../../../elements/Select/Select';
import type { Category } from '../../../../../../entities';

type State = {
  detailCount: number;
  revisionHintModalOpen: boolean;
  errorLinkOpen: boolean;
  ebayInformationOpen: boolean;
  errorLinkFirstTimeOpen: boolean;
};

const maxCharactersDetailCount = 100;

export class ProductBox extends Component<NewProductFormProps, State> {
  state: State = {
    detailCount: maxCharactersDetailCount,
    revisionHintModalOpen: false,
    errorLinkOpen: false,
    ebayInformationOpen: false,
    errorLinkFirstTimeOpen: false,
  };
  
  componentDidMount() {
    const { url } = this.props;
    if (url.errors) {
      this.setState({
        errorLinkOpen: true,
        errorLinkFirstTimeOpen: true,
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { url } = this.props;
    const { errorLinkFirstTimeOpen } = this.state;

    const u = url.errors ? url.errors[0] : undefined;
    const p =  prevProps.url.errors ? prevProps.url.errors[0] : undefined;
    
    if ((!p && !!u) || (!!p && !!u && !errorLinkFirstTimeOpen)) {
      if (['ebayAuction', 'notCartUrlFormat', 'amazonSpain'].includes(u)) {
        this.setState({
          errorLinkOpen: true,
          errorLinkFirstTimeOpen: true,
        });
      }
    }
  }

  handleChangeTextarea = (e: Event) => {
    const { handleChangeInputText } = this.props;
    if (e.target instanceof HTMLTextAreaElement) {
      const { value } = e.target;
      const text = value.length ? value.slice(0, maxCharactersDetailCount) : '';
      this.setState({ detailCount: maxCharactersDetailCount - text.length });
      handleChangeInputText(e, text);
    }
  };

  handleClickDeleteImage = (e: Event) => {
    const { handleDeleteImage } = this.props;
    e.preventDefault();
    e.stopPropagation();
    handleDeleteImage();
  };

  handleClickOpenEbayModal = () => {
    const { ebayInformationOpen } = this.state;
    this.setState({
      ebayInformationOpen: true,
    });
  };

  render() {
    const {
      locale,
      localValues,
      url,
      handleChangeInputUrl,
      handleFocusInputText,
      handleOnKeyPress,
      isScrapping,
      name,
      handleChangeInputText,
      scrappedProduct,
      detailOptions,
      handleChangeDetailOption,
      amount,
      handleChangeAmount,
      categoryId,
      categories,
      handleChangeCategory,
      weight,
      weightUnit,
      handleBlurCleave,
      handleChangeCleave,
      handleChangeWeightUnit,
      handleWeightInit,
      shipping,
      handleShippingInit,
      handleOpenShippingHelpModal,
      price,
      handlePriceInit,
      image,
      handleChangeImage,
      description,
      volumeTier,
      handleChangeVolume,
    } = this.props;

    const { detailCount, errorLinkOpen, ebayInformationOpen } = this.state;

    const categoryOptions: Option[] = (categories || [])
      .filter((category: Category) => category.enabled)
      .map((category: Category) => {
        return {
          value: category.id,
          label: category.translations[locale.locale.toUpperCase()].name,
        };
      });

    const hasDetailOptions: boolean =
      !!scrappedProduct && !!scrappedProduct.detailOptions;

    const hasVolumetric: boolean =
      !!localValues.categoriesMap &&
      !!categoryId.value &&
      !!localValues.categoriesMap[`${categoryId.value}`] &&
      localValues.categoriesMap[`${categoryId.value}`].volumeTiers.length !== 0;

    const hasWeight: boolean =
      !!localValues.categoriesMap &&
      !!categoryId.value &&
      localValues.categoriesMap[`${categoryId.value}`].criteria === 'WEIGHT';

    const urlError = url.errors ? FormValidator.buildErrorLabel(url.errors, {
        required: 'newOrder.product.form.url.error.required',
        urlFormat: 'newOrder.product.form.url.error.format',
        notCartUrlFormat: 'newOrder.product.form.url.error.notCartUrlFormat',
        amazonSpain: 'newOrder.product.form.url.error.amazonSpain',
        ebayAuction: 'newOrder.product.form.url.error.ebayAuction',
        unscrapped: 'newOrder.product.form.url.error.unscrapped',
        // invalidDomain: 'newOrder.product.form.url.error.invalidDomain',
        // urlNotStore: 'newOrder.product.form.url.error.urlNotStore',
        // storeBlacklist: 'newOrder.product.form.url.error.storeBlacklist',
      }, url.errors && url.errors[0] === 'unscrapped' ? (
      <Fragment>
        Ingrésala manualmente. <a href="https://youtu.be/DDUPRRTBhrg" target="_blank">Ver ejemplo aquí.</a>
      </Fragment>) : undefined) : undefined;

    const nameError =
      !!name.errors &&
      FormValidator.buildErrorLabel(name.errors, {
        required: 'newOrder.product.form.name.error.required',
        unscrapped: 'newOrder.product.form.name.error.unscrapped',
      });

    const imageError =
      !!image.errors &&
      FormValidator.buildErrorLabel(image.errors, {
        required: 'newOrder.product.form.uploadImage.error.required',
        unscrapped: 'newOrder.product.form.uploadImage.error.unscrapped',
      });

    const categoryError =
      !!categoryId.errors &&
      FormValidator.buildErrorLabel(categoryId.errors, {
        required: 'newOrder.product.form.category.error.required',
        unscrapped: 'newOrder.product.form.category.error.unscrapped',
      });

    const weightError =
      !!weight.errors &&
      FormValidator.buildErrorLabel(weight.errors, {
        required: 'newOrder.product.form.weight.error.required',
        greaterThanZero: 'newOrder.product.form.weight.error.greaterThanZero',
        lessThanFiveKg: 'newOrder.product.form.weight.error.lessThanFiveKg',
        unscrapped: 'newOrder.product.form.weight.error.unscrapped',
      });

    const priceError =
      !!price.errors &&
      FormValidator.buildErrorLabel(price.errors, {
        required: 'newOrder.product.form.price.error.required',
        unscrapped: 'newOrder.product.form.price.error.unscrapped',
      });


    const ebayButton = url && !url.errors && ebayUrlRegex.test(url.value) &&
    <button className={style.ebayButtonWrap} onClick={this.handleClickOpenEbayModal}>
      Información importante para productos de eBay. Ver más.
    </button>;

    const shippingSection = (
      <Fragment>
        <FormattedMessage id='newOrder.product.form.shipping.label'>
          {(txt) => (
            <label className={formStyle.label}>
              {txt}:
              <button
                className={style.help}
                onClick={handleOpenShippingHelpModal}
              >
                <div className={style.tooltipIcon}>?</div>
              </button>
            </label>
          )}
        </FormattedMessage>
        {handleChangeCleave && handleShippingInit && (
          <InputPrice
            name='shipping'
            value={shipping}
            hasError={!!shipping}
            handleFocusInputText={handleFocusInputText}
            handleChangeCleave={handleChangeCleave}
            handleBlurCleave={handleBlurCleave}
            handleInit={handleShippingInit}
          />
        )}
      </Fragment>
    );
    
    const hintIntl = 'newOrder.product.form.price.tooltip';
    const toolTip = <Tooltip id={hintIntl} intl={hintIntl}/>;
    
    return (
      <Fragment>
        <div className={style.productBoxWrap}>
          <h2 className={style.title}>1. Información de producto</h2>
          <div className={formStyle.formWrap}>
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id='newOrder.product.form.url.label'>
                {(txt) => <label className={formStyle.label}>{txt}</label>}
              </FormattedMessage>
              <div className={style.inputWrap}>
                <FormattedMessage id='newOrder.product.form.url.placeholder'>
                  {(txt) => (
                    <input
                      type='text'
                      name='url'
                      autoComplete='disabled'
                      value={url.value}
                      className={classNames(
                        inputStyle.input,
                        style.urlInput,
                        { [inputStyle.red]: !!url.errors },
                        {
                          [inputStyle.yellow]:
                            !!url.errors && url.errors[0] === 'unscrapped',
                        },
                        { [inputStyle.lightBlue]: !!url.value }
                      )}
                      onChange={handleChangeInputUrl}
                      onFocus={handleFocusInputText}
                      placeholder={txt}
                      onKeyPress={handleOnKeyPress}
                    />
                  )}
                </FormattedMessage>
                {isScrapping && (
                  <Loading
                    type='sk-three-bounce'
                    color='lightBlue'
                    number={3}
                    className={style.loading}
                  />
                )}
                {urlError}
                {ebayButton}
              </div>
            </div>
            <div className={classNames(formStyle.row, formStyle.inputRow)}>
              <FormattedMessage id='newOrder.product.form.name.label'>
                {(txt) => <label className={formStyle.label}>{txt}:</label>}
              </FormattedMessage>
              <FormattedMessage id='newOrder.product.form.name.placeholder'>
                {(txt) => (
                  <input
                    type='text'
                    name='name'
                    id='name'
                    autoComplete='disabled'
                    value={name.value}
                    className={classNames(inputStyle.input, {
                      [inputStyle.red]: !!name.errors,
                      [inputStyle.yellow]:
                        !!name.errors && name.errors[0] === 'unscrapped',
                      [inputStyle.lightBlue]: !!name.value,
                    })}
                    onChange={handleChangeInputText}
                    onFocus={handleFocusInputText}
                    placeholder={txt}
                  />
                )}
              </FormattedMessage>
              {nameError}
            </div>

            <div className={style.dualWrap}>
              <div className={style.imageWrap}>
                <div className={style.imageContent}>
                  {(image.value || image.valueURI) && (
                    <button
                      key='newOrderImageDelete'
                      type='button'
                      className={style.dropzoneDeleteImage}
                      onClick={this.handleClickDeleteImage}
                    >
                      <i className={icons.iconCloseLight} />
                    </button>
                  )}
                  <Element name='image'>
                    <Dropzone
                      key='dropzone'
                      accept='image/jpeg,image/jpg,image/png'
                      multiple={false}
                      className={classNames(style.dropzone, {
                        [style.error]:
                          image &&
                          image.errors &&
                          image.errors[0] !== 'unscrapped',
                        [style.yellow]:
                          image &&
                          image.errors &&
                          image.errors[0] === 'unscrapped',
                      })}
                      activeClassName={style.active}
                      disabledClassName={style.disabled}
                      maxSize={2097152}
                      onDrop={handleChangeImage}
                    >
                      {image && (image.value || image.valueURI) && (
                        <img
                          className={style.preview}
                          src={image.value || image.valueURI}
                          alt={name.value || ''}
                        />
                      )}

                      {(!image || (!image.value && !image.valueURI)) && (
                        <Fragment>
                          <img
                            className={style.pictureIcon}
                            src={picture}
                            alt='picture'
                          />
                          <p>Subir imagen</p>
                        </Fragment>
                      )}
                    </Dropzone>
                    {imageError}
                    <InputQuantityRound
                      disableLess={amount.value === 1}
                      disableMore={amount.value === 5}
                      quantity={amount ? `${amount.value}` : `1`}
                      handleChange={handleChangeAmount}
                    />
                  </Element>
                </div>
              </div>
              <div className={style.formWrap}>
                {hasDetailOptions && scrappedProduct && (
                  <div className={style.detailsWrap}>
                    {Object.keys(
                      scrappedProduct.detailOptions.variationDimensions
                    ).map((key: string, idx: number) => (
                      <DetailOption
                        key={idx}
                        dimension={key}
                        handleChange={handleChangeDetailOption}
                        selectedOption={detailOptions[key]}
                        options={
                          scrappedProduct.detailOptions.variationDimensions[key]
                        }
                      />
                    ))}
                  </div>
                )}
                <div className={classNames(formStyle.row, formStyle.inputRow)}>
                  <FormattedMessage id='newOrder.product.form.category.label'>
                    {(txt) => <label className={formStyle.label}>{txt}</label>}
                  </FormattedMessage>
                  <FormattedMessage id='newOrder.product.form.category.placeholder'>
                    {(txt) => (
                      <Select
                        options={categoryOptions}
                        id='categoryId'
                        className={classNames({
                          'light-blue': categoryId && categoryId.value,
                          red:
                            categoryId &&
                            categoryId.errors &&
                            categoryId.errors[0] !== 'unscrapped',
                          yellow:
                            categoryId &&
                            categoryId.errors &&
                            categoryId.errors[0] === 'unscrapped',
                        })}
                        value={categoryId.value}
                        hasError={!!categoryId.errors}
                        placeholder={txt}
                        handleChange={handleChangeCategory}
                      />
                    )}
                  </FormattedMessage>
                  {categoryError}
                </div>
                {hasVolumetric && localValues.categoriesMap && (
                  <section
                    className={classNames(formStyle.row, formStyle.inputRow)}
                  >
                    <Element name='volumeTier'>
                      <VolumetricOptions
                        value={volumeTier.value}
                        onChange={handleChangeVolume}
                        localeState={locale}
                        errors={volumeTier.errors}
                        category={
                          localValues.categoriesMap[`${categoryId.value}`]
                        }
                      />
                    </Element>
                  </section>
                )}
                <div className={style.groupWrap}>
                  {!hasWeight && (
                    <div
                      className={classNames(
                        formStyle.row,
                        formStyle.inputRow,
                        style.shippingWrap
                      )}
                    >
                      {shippingSection}
                    </div>
                  )}
                  {hasWeight && (
                    <div
                      className={classNames(
                        formStyle.row,
                        formStyle.inputRow,
                        style.weightWrap
                      )}
                    >
                      <FormattedMessage id='newOrder.product.form.weight.label'>
                        {(txt) => (
                          <label className={formStyle.label}>{txt}</label>
                        )}
                      </FormattedMessage>
                      <InputWeight
                        name='weight'
                        value={weight.value}
                        hasError={!!weight.errors}
                        errors={weight.errors}
                        weightUnit={weightUnit}
                        handleBlurCleave={handleBlurCleave}
                        handleFocusInputText={handleFocusInputText}
                        handleChangeCleave={handleChangeCleave}
                        handleChangeWeightUnit={handleChangeWeightUnit}
                        handleInit={handleWeightInit}
                      />
                      {weightError}
                    </div>
                  )}
                  <div
                    className={classNames(
                      formStyle.row,
                      formStyle.inputRow,
                      style.priceWrap
                    )}
                  >
                    <FormattedMessage id='newOrder.product.form.price.label'>
                      {(txt) => (
                        <label
                          className={classNames(formStyle.label, style.row)}
                        >
                          {txt} {toolTip}
                        </label>
                      )}
                    </FormattedMessage>
                    <InputPrice
                      name='price'
                      value={price.value}
                      hasError={!!price.errors}
                      errors={price.errors}
                      handleFocusInputText={handleFocusInputText}
                      handleChangeCleave={handleChangeCleave}
                      handleBlurCleave={handleBlurCleave}
                      handleInit={handlePriceInit}
                    />
                    {priceError}
                  </div>
                </div>
                {hasWeight && (
                  <div
                    className={classNames(formStyle.row, formStyle.inputRow)}
                  >
                    {shippingSection}
                  </div>
                )}
                {!hasDetailOptions && (
                  <div
                    className={classNames(
                      formStyle.row,
                      formStyle.inputRow,
                      formStyle.rowNoMargin,
                      style.description
                    )}
                  >
                    <FormattedMessage id='newOrder.product.form.description.label'>
                      {(txt) => (
                        <label className={formStyle.label}>
                          {txt}:
                          <span className={formStyle.optional}>
                            {this.context.intl.formatMessage({
                              id: 'newOrder.product.form.url.optional.label',
                            })}
                          </span>
                        </label>
                      )}
                    </FormattedMessage>
                    <FormattedMessage id='newOrder.product.form.description.count'>
                      {(txt) => (
                        <label
                          className={classNames(formStyle.label, style.counter)}
                        >
                          {detailCount}
                          {detailCount === 1
                            ? ` ${txt}`
                            : ` ${txt.replace('Ã¡', 'a')}es`}
                        </label>
                      )}
                    </FormattedMessage>
                    <FormattedMessage id='newOrder.product.form.description.placeholder'>
                      {(txt) => (
                        <textarea
                          name='description'
                          autoComplete='disabled'
                          value={description.value}
                          className={classNames(inputStyle.input, {
                            [inputStyle.lightBlue]:
                              description && description.value,
                          })}
                          onChange={this.handleChangeTextarea}
                          onFocus={handleFocusInputText}
                          placeholder={txt}
                        />
                      )}
                    </FormattedMessage>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <ErrorLinkModal
          isOpen={errorLinkOpen}
          handleClose={() => this.setState({ errorLinkOpen: false })}
          error={url.errors && url.errors[0] ? (url.errors[0]: any) : undefined}
        />
        <EbayInformationModal
          isOpen={ebayInformationOpen}
          handleClose={() => this.setState({ ebayInformationOpen: false })}
          error={url.errors && url.errors[0] ? (url.errors[0]: any) : undefined}
        />
      </Fragment>
    );
  }
}

ProductBox.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/components/NewProductForm/ProductBox/ProductBox.jsx